import { skipToken } from "@reduxjs/toolkit/query";

import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import {
  AdminInstrument,
  AdminInstrumentsArgs,
  useGetAdminInstrumentQuery,
  useGetAdminInstrumentsQuery,
} from "../../../../services/admin";
import { rejectionStatusOf } from "../../../../services/rejectionStatus";
import { toSectionState } from "../../utils/sectionState";
import {
  DEFAULT_FILTERS,
  INSTRUMENT_SEARCH_DEBOUNCE_MS,
  INSTRUMENT_SEARCH_MAX_LENGTH,
} from "../constants";
import { useInstrumentCatalogSession } from "../InstrumentCatalogProvider";
import { CatalogFilters, CatalogSelection } from "../types";

const NO_INSTRUMENTS: AdminInstrument[] = [];
const NOT_FOUND = 404;

const toArgs = (
  query: string,
  { assetType, price }: CatalogFilters,
): AdminInstrumentsArgs => ({
  ...(query && { query }),
  ...(assetType !== "all" && { assetType }),
  ...(price === "stale" && { stale: true as const }),
});

export const useInstrumentCatalog = () => {
  const { filters, selectedId, changeFilters } = useInstrumentCatalogSession();
  const debouncedQuery = useDebouncedValue(
    filters.query.trim(),
    INSTRUMENT_SEARCH_DEBOUNCE_MS,
  );
  const listArgs = toArgs(debouncedQuery, filters);
  const listQuery = useGetAdminInstrumentsQuery(listArgs);

  // currentData, so a failed read after a filter change never shows the previous filter's rows.
  const section = toSectionState({
    data: listQuery.currentData,
    isError: listQuery.isError,
  });
  const isAvailable = section.state === "available";
  const instruments = isAvailable ? section.data.instruments : NO_INSTRUMENTS;

  // Read by id as well, because a filter or the row cap can hide the selected row.
  const selectedQuery = useGetAdminInstrumentQuery(selectedId ?? skipToken);
  const selectedRow = instruments.find(({ id }) => id === selectedId);
  const refetchSelected = () => {
    if (selectedId !== null) {
      void selectedQuery.refetch();
    }
  };
  const selection: CatalogSelection = {
    instrument: selectedQuery.currentData ?? selectedRow ?? null,
    isHidden: isAvailable && !selectedRow,
    isNotFound: rejectionStatusOf(selectedQuery.error) === NOT_FOUND,
    isFailed: selectedQuery.isError,
    retry: refetchSelected,
  };

  const handleChange = (changes: Partial<CatalogFilters>) =>
    changeFilters(
      changes.query === undefined
        ? changes
        : {
            ...changes,
            query: changes.query.slice(0, INSTRUMENT_SEARCH_MAX_LENGTH),
          },
    );

  return {
    section,
    filters,
    instruments,
    selection,
    total: isAvailable ? section.data.total : 0,
    // From the args, not the live filters: the rows on screen are the ones those args fetched.
    isFiltered: Object.keys(listArgs).length > 0,
    changeFilters: handleChange,
    clearFilters: () => changeFilters(DEFAULT_FILTERS),
    isRefreshing: listQuery.isFetching,
    refresh: () => {
      void listQuery.refetch();
      refetchSelected();
    },
  };
};
